import React from 'react';
import styled from 'styled-components';

const StyledButton = styled.button`
  background: ${({ theme }) => theme.buttonBg};
  border: none;
  border-radius: 9999px;
  padding: ${({ size }) => {
    switch (size) {
      case 'small':
        return '0.5rem 1.25rem';
      case 'large':
        return '1rem 2.25rem';
      default:
        return '0.75rem 1.75rem';
    }
  }};
  font-size: ${({ size }) => {
    switch (size) {
      case 'small':
        return '0.875rem';
      case 'large':
        return '1.125rem';
      default:
        return '1rem';
    }
  }};
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.buttonShadow};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      ${({ theme }) => theme.text}10,
      transparent
    );
    transform: translateX(-100%);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.buttonShadow}, 0 10px 20px rgba(0, 0, 0, 0.1);

    &:before {
      transform: translateX(100%);
      transition: transform 0.6s ease;
    }
  }

  &:active {
    transform: translateY(1px);
    box-shadow: ${({ theme }) => theme.buttonShadow};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    &:hover {
      transform: none;
      box-shadow: ${({ theme }) => theme.buttonShadow};
    }
  }

  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background: linear-gradient(145deg, ${theme.primary}, ${theme.primary}dd);
    color: white;
    &:hover {
      background: linear-gradient(145deg, ${theme.primary}cc, ${theme.primary}aa);
    }
  `}

  ${({ variant, theme }) =>
    variant === 'outlined' &&
    `
    background: transparent;
    border: 2px solid ${theme.primary};
    color: ${theme.primary};
    box-shadow: none;
    
    &:hover {
      background: ${theme.primary}25;
      box-shadow: none;
    }
  `}

  @media (max-width: 768px) {
    padding: ${({ size }) => {
      switch (size) {
        case 'small':
          return '0.375rem 0.75rem';
        case 'large':
          return '0.875rem 1.75rem';
        default:
          return '0.625rem 1.25rem';
      }
    }};
  }

  @media (max-width: 480px) {
    padding: ${({ size }) => {
      switch (size) {
        case 'small':
          return '0.25rem 0.5rem';
        case 'large':
          return '0.75rem 1.5rem';
        default:
          return '0.5rem 1rem';
      }
    }};
  }

  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: ${({ theme }) => theme.buttonShadow};
    }
  }
`;

const Button = ({
  children,
  size = 'medium',
  variant = 'default',
  disabled = false,
  onClick,
  ...props
}) => {
  return (
    <StyledButton
      size={size}
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button; 